/**
 * Cart State Reducer
 * Ported from legacy nv-nav-cart-build (lines 1756-2107)
 */

import { actionTypes, CartAction, CartStateInterface, CartProduct } from '../actions/cartActions';
import { ProductContext } from '../../product/product.context';

// Declare global helper objects used in the reducer
declare const nvCartHelpers: any;
declare const nvProductHelpers: any;

/**
 * Course line items use ids like `course:15853440_16593544` (course key + `_` + instance id).
 * Returns null for any other id shape so legacy products are unchanged.
 */
function parseCourseProductId(productId: string): { courseKey: string; instanceId: string } | null {
  const id = String(productId);
  if (!id.startsWith('course:') || id.indexOf('_') === -1) {
    return null;
  }
  const u = id.indexOf('_');
  const courseKey = id.slice(0, u);
  const instanceId = id.slice(u + 1);
  if (!courseKey || !instanceId) {
    return null;
  }
  return { courseKey, instanceId };
}

/**
 * State Reducer for Cart Management
 * @param state Current cart state
 * @param action Action to be performed
 * @returns New cart state
 */
export function stateReducer(
  state: CartStateInterface | undefined,
  action: CartAction
): CartStateInterface {
  let newState: CartStateInterface;

  if (state === undefined) {
    newState = {
      cart: [],
      lastAction: null,
    };
  } else {
    newState = {
      cart: state.cart.slice(),
      lastAction: action,
    };
  }

  switch (action.type) {
    case actionTypes.SET_CART:
      // Preserve lastAction if provided in action, otherwise set to null
      return {
        cart: action.cart || [],
        lastAction: action.lastAction !== undefined ? action.lastAction : null
      };

    case actionTypes.ADD_ITEM_TO_CART: {
      const parsedAdd = parseCourseProductId(String(action.id));
      if (parsedAdd) {
        const idxSameCourse = newState.cart.findIndex((p: CartProduct) => {
          const pl = parseCourseProductId(String(p.id));
          return pl != null && pl.courseKey === parsedAdd.courseKey;
        });

        if (idxSameCourse !== -1) {
          const existing = newState.cart[idxSameCourse];
          const sameFullId = String(existing.id) == String(action.id);

          if (!sameFullId) {
            const cartProductsList = ProductContext.getCartProductsList();
            const cartProduct = cartProductsList.filter(function (bcProduct: any) {
              return bcProduct.id == action.id;
            })[0];

            if (cartProduct) {
              newState.cart = newState.cart.filter((p: CartProduct) => {
                const pl = parseCourseProductId(String(p.id));
                return pl == null || pl.courseKey !== parsedAdd.courseKey;
              });

              const qty = parseInt(String(action.quantity), 10);
              const unitPriceNum = parseFloat(String(cartProduct.unitPrice)) || 0;
              const totalPrice = (qty * unitPriceNum).toFixed(2);
              const availableQuantity = cartProduct.inventoryStatus.availableQuantity || null;

              const expireDate = new Date();
              expireDate.setDate(expireDate.getDate() + 2);

              newState.cart.push({
                id: cartProduct.id,
                sku: cartProduct.sku,
                name: cartProduct.displayName,
                isQuantity: true,
                category: action.category,
                activeTab: action.category,
                quantity: qty,
                productType: cartProduct.productType,
                listPrice: cartProduct.salePriceWithQuantity,
                unitPrice: cartProduct.unitPrice,
                pricing: {
                  listPriceWithQuantity: cartProduct.salePriceWithQuantity * qty,
                  salePriceWithQuantity: cartProduct.salePriceWithQuantity * qty,
                  unitPrice: cartProduct.unitPrice
                },
                totalPrice: totalPrice,
                productIsInStock: cartProduct.inventoryStatus.productIsInStock,
                productIsAllowsBackorders: cartProduct.inventoryStatus.productIsAllowsBackorders,
                expires: expireDate,
                locale: cartProduct.locale,
                minimumQuantity: cartProduct.minimumQuantity || 1,
                limitPerCustomer: cartProduct.maximumQuantity,
                availableQuantity: availableQuantity,
                promo: cartProduct.relatedProducts
              });

              return {
                cart: newState.cart,
                lastAction: action,
                activeTab: action.category
              };
            }
          }
        }
      }

      if (nvCartHelpers.productIsInCart(newState.cart, action.id)) {
        // Product already in cart - update quantity
        const newCart = newState.cart.map(function (product: CartProduct) {
          let expireDate: Date;
          if ("expires" in product) {
            expireDate = product.expires;
          } else {
            expireDate = new Date();
            expireDate.setDate(expireDate.getDate() + 2);
          }

          if (product.id === action.id) {
            const newQuantity = parseInt(String(product.quantity), 10) + parseInt(String(action.quantity), 10);
            const cartProductsList = ProductContext.getCartProductsList();
            const cartProduct = cartProductsList.filter(function (bcProduct: any) {
              return bcProduct.id == action.id;
            })[0];

            if (nvProductHelpers.productLimitReached(newState.cart, action.id)) {
              return product;
            } else {
              // Ensure unitPrice is parsed as a number to avoid string multiplication issues
              const unitPriceNum = parseFloat(String(cartProduct.unitPrice)) || 0;
              const totalPrice = (newQuantity * unitPriceNum).toFixed(2);
              const availableQuantity = cartProduct.inventoryStatus.availableQuantity || null;

              return {
                id: product.id,
                sku: product.sku,
                name: product.name,
                isQuantity: true,
                quantity: newQuantity,
                activeTab: action.category,
                category: action.category,
                productType: product.productType,
                listPrice: cartProduct.salePriceWithQuantity,
                unitPrice: cartProduct.unitPrice,
                pricing: product.pricing,
                totalPrice: totalPrice,
                productIsInStock: cartProduct.inventoryStatus.productIsInStock,
                productIsAllowsBackorders: cartProduct.inventoryStatus.productIsAllowsBackorders,
                expires: expireDate,
                locale: cartProduct.locale,
                minimumQuantity: cartProduct.minimumQuantity || 1,
                limitPerCustomer: cartProduct.maximumQuantity,
                availableQuantity: availableQuantity,
                promo: cartProduct.relatedProducts
              };
            }
          } else {
            return product;
          }
        });

        return {
          cart: newCart,
          lastAction: action,
          activeTab: action.category
        };
      } else {
        // New product - add to cart
        const cartProductsList = ProductContext.getCartProductsList();
        const cartProduct = cartProductsList.filter(function (bcProduct: any) {
          return bcProduct.id == action.id;
        })[0];
        // Ensure unitPrice is parsed as a number to avoid string multiplication issues
        const unitPriceNum = parseFloat(String(cartProduct.unitPrice)) || 0;
        const totalPrice = (parseInt(String(action.quantity), 10) * unitPriceNum).toFixed(2);
        const availableQuantity = cartProduct.inventoryStatus.availableQuantity || null;

        const expireDate = new Date();
        expireDate.setDate(expireDate.getDate() + 2);

        newState.cart.push({
          id: cartProduct.id,
          sku: cartProduct.sku,
          name: cartProduct.displayName,
          isQuantity: true,
          category: action.category,
          activeTab: action.category,
          quantity: parseInt(String(action.quantity), 10),
          productType: cartProduct.productType,
          listPrice: cartProduct.salePriceWithQuantity,
          unitPrice: cartProduct.unitPrice,
          pricing: {
            listPriceWithQuantity: cartProduct.salePriceWithQuantity * parseInt(String(action.quantity), 10),
            salePriceWithQuantity: cartProduct.salePriceWithQuantity * parseInt(String(action.quantity), 10),
            unitPrice: cartProduct.unitPrice
          },
          totalPrice: totalPrice,
          productIsInStock: cartProduct.inventoryStatus.productIsInStock,
          productIsAllowsBackorders: cartProduct.inventoryStatus.productIsAllowsBackorders,
          expires: expireDate,
          locale: cartProduct.locale,
          minimumQuantity: cartProduct.minimumQuantity || 1,
          limitPerCustomer: cartProduct.maximumQuantity,
          availableQuantity: availableQuantity,
          promo: cartProduct.relatedProducts
        });

        return newState;
      }
    }

    case actionTypes.INC_ITEM_QUANTITY:
      newState.cart = newState.cart.map(function (product: CartProduct) {
        let expireDate: Date;
        if ("expires" in product) {
          expireDate = product.expires;
        } else {
          expireDate = new Date();
          expireDate.setDate(expireDate.getDate() + 2);
        }

        if (product.id == action.id) {
          const newQuantity = product.quantity + 1;

          if (nvProductHelpers.productLimitReached(newState.cart, action.id)) {
            return product;
          } else {
            // Ensure unitPrice is parsed as a number to avoid string multiplication issues
            const unitPriceNum = parseFloat(String(product.unitPrice)) || 0;
            const totalPrice = (newQuantity * unitPriceNum).toFixed(2);

            return {
              id: product.id,
              sku: product.sku,
              activeTab: action.category,
              category: action.category,
              name: product.name,
              expires: expireDate,
              isQuantity: true,
              quantity: newQuantity,
              locale: product.locale,
              totalPrice: totalPrice,
              listPrice: product.listPrice,
              unitPrice: product.unitPrice,
              pricing: product.pricing,
              productType: product.productType,
              minimumQuantity: product.minimumQuantity,
              limitPerCustomer: product.limitPerCustomer,
              availableQuantity: product.availableQuantity,
              productIsInStock: product.productIsInStock,
              productIsAllowsBackorders: product.productIsAllowsBackorders,
              promo: product.promo,
            };
          }
        } else {
          return product;
        }
      });

      return newState;

    case actionTypes.DEC_ITEM_QUANTITY:
      newState.cart = newState.cart.map(function (product: CartProduct) {
        let expireDate: Date;
        if ("expires" in product) {
          expireDate = product.expires;
        } else {
          expireDate = new Date();
          expireDate.setDate(expireDate.getDate() + 2);
        }

        if (product.id == action.id && product.quantity > 1) {
          const newQuantity = product.quantity - 1;
          // Ensure unitPrice is parsed as a number to avoid string multiplication issues
          const unitPriceNum = parseFloat(String(product.unitPrice)) || 0;
          const totalPrice = (newQuantity * unitPriceNum).toFixed(2);

          return {
            id: product.id,
            sku: product.sku,
            name: product.name,
            expires: expireDate,
            locale: product.locale,
            activeTab: action.category,
            category: action.category,
            isQuantity: true,
            quantity: newQuantity,
            totalPrice: totalPrice,
            listPrice: product.listPrice,
            unitPrice: product.unitPrice,
            pricing: product.pricing,
            productType: product.productType,
            productIsInStock: product.productIsInStock,
            minimumQuantity: product.minimumQuantity,
            limitPerCustomer: product.limitPerCustomer,
            availableQuantity: product.availableQuantity,
            productIsAllowsBackorders: product.productIsAllowsBackorders,
            promo: product.promo,
          };
        } else {
          return product;
        }
      });

      return newState;

    case actionTypes.UPDATE_PRODUCTS:
      newState.cart = newState.cart.map(function (product: CartProduct) {
        let expireDate: Date;
        if ("expires" in product) {
          expireDate = product.expires;
        } else {
          expireDate = new Date();
          expireDate.setDate(expireDate.getDate() + 2);
        }

        const cartProduct = (action.products || []).filter(
          function (drProductItem: any) {
            return drProductItem.id == product.id;
          }
        )[0];

        if (cartProduct === undefined) {
          // If we can't find digital river product, just return our stored one
          return product;
        }

        const totalPrice = (
          product.quantity * cartProduct.unitPrice
        ).toFixed(2);

        return {
          id: product.id,
          sku: product.sku,
          name: cartProduct.displayName && cartProduct.displayName.includes('-')
            ? cartProduct.displayName.split("-")[0]
            : cartProduct.displayName,
          isQuantity: true,
          quantity: product.quantity,
          activeTab: action.category,
          category: action.category,
          listPrice: cartProduct.salePriceWithQuantity,
          unitPrice: cartProduct.unitPrice,
          pricing: {
            listPriceWithQuantity: cartProduct.salePriceWithQuantity * product.quantity,
            salePriceWithQuantity: cartProduct.salePriceWithQuantity * product.quantity,
            unitPrice: cartProduct.unitPrice
          },
          totalPrice: totalPrice,
          productType: cartProduct.productType,
          productIsInStock: cartProduct.inventoryStatus.productIsInStock,
          productIsAllowsBackorders: cartProduct.inventoryStatus.productIsAllowsBackorders,
          expires: expireDate,
          locale: cartProduct.locale,
          minimumQuantity: cartProduct.minimumQuantity || 1,
          limitPerCustomer: product.limitPerCustomer,
          availableQuantity: product.availableQuantity,
          promo: product.promo,
        };
      });

      return newState;

    case actionTypes.REMOVE_ITEM_FROM_CART:
      const removedProduct = state.cart.find(p => p.id == action.id);

      if (removedProduct) {
        newState.lastAction = { ...action, quantityRemoved: removedProduct.quantity };
      }

      newState.cart = newState.cart.filter(p => p.id != action.id);

      return newState;

    default:
      return newState;
  }
}

// Make reducer available globally (for legacy compatibility)
if (typeof window !== 'undefined') {
  if ((window as any).stateReducer == null) {
    (window as any).stateReducer = stateReducer;
  }
}